import { C1_BaseObject } from "./C1_BaseObject";
import { isPdfOverlayActive, pdfDrawingBounds } from "../lib/C1_pdfOverlay";
import { nextEloSelectionFocus } from "../plan/C1_eloSelectionFocus";
import { type C1_Project } from "./C1_Project";
import WatchableValue from "../../../src/models/WatchableValue";
import BaseObject from "../../../src/models/BaseObject";
import { C1_ObjectRegistry } from "./C1_ObjectRegistry";
import { C1_Furniture } from "./C1_Furniture";
import { C1_Room } from "./C1_Room";
import { furnitureCategoryForRoomType } from "../furniture/C1_roomTypeFurnitureCategory";
import { C1_Wall } from "./C1_Wall";
import { wallMergeRooms } from "./C1_mergeRooms";
import { C1_Floor } from "./C1_Floor";
import C1_EloItem from "./C1_EloItem";
import type { C1_AxisData, C1_ProjectData, FurnitureCategory, WithOptionalId } from "../types/C1_ModelTypes";
import { C1_FloorFinish, RoomType } from "../types/C1_ModelTypes";
import { toC1Language, type C1_Language } from "../C1_language";
import type {
  Drag,
  DropPreview,
  EditMode,
  ObjectDragPreview,
  PaletteTool,
  Rect,
  ResizePreview,
  RoomDraft,
  SaveState,
} from "../types/C1_AppTypes";
import { ApplicationMode } from "../../../src/models/SettingsEnums";
import { EventLocker } from "../../../src/eventLogger/EventLocker";
import { isElectricalFurnitureId } from "../furniture/C1_electricalFurniture";
import Floor from "../../../src/models/Floor";
import type BaseModel from "../../../src/models/BaseModel";
import type { State } from "../../../src/State";
import { registerC1StateFactory } from "./C1_runtimeContext";
import { loadDefinitions, type C1_Catalog } from "./C1_Catalog";
import Connection from "../../../src/models/Connection";
import { showDeletionPreventedToast } from "../../../src/components/showDeletionPreventedToast";
import { copyFurniture, pasteFurniture, type C1_FurnitureClipboard } from "./C1_FurnitureClipboard";
import { copyOpening, pasteOpening, type C1_OpeningClipboard } from "./C1_OpeningClipboard";
import { C1_Opening } from "./C1_Opening";
import { generateTags } from "../estada/C1_generateTags";
import { itx } from "../../../src/translationItx/itx";
import { showErrToast } from "../../../src/components/Toasts";
import { C1_Consumer } from "./C1_Consumer";
import { C1_Button } from "./C1_Button";
import { paletteIdForExistingDevice } from "./C1_placeExistingElectricalDevice";
import { placeNativeClipboard } from "./C1_NativeClipboard";
import { furnitureResolvers } from "../furniture/C1_furnitureResolvers";

export const C1_MAX_ZOOM = 10;

const DEBUG_FLAGS_STORAGE_KEY = "concept-one.debug-flags";

type C1_DebugFlags = {
  showDebugFurnitureOrigins: boolean;
  showDebugEloItemPositions: boolean;
  showDebugEloGroupPositions: boolean;
};

function loadDebugFlags(): C1_DebugFlags {
  const defaults: C1_DebugFlags = {
    showDebugFurnitureOrigins: false,
    showDebugEloItemPositions: false,
    showDebugEloGroupPositions: false,
  };
  if (typeof window === "undefined") return defaults;

  try {
    const stored = JSON.parse(
      window.localStorage.getItem(DEBUG_FLAGS_STORAGE_KEY) ?? "null",
    ) as Partial<C1_DebugFlags> | null;
    if (!stored) return defaults;
    return {
      showDebugFurnitureOrigins: stored.showDebugFurnitureOrigins === true,
      showDebugEloItemPositions: stored.showDebugEloItemPositions === true,
      showDebugEloGroupPositions: stored.showDebugEloGroupPositions === true,
    };
  } catch {
    return defaults;
  }
}

const initialDebugFlags = loadDebugFlags();

export type C1_FloorImageRenderOptions = {
  includeAxes: boolean;
  includeDimensions: boolean;
  includeRoomNames?: boolean;
  subtleMasterIndices?: boolean;
  roomPlanExport?: boolean;
  /** Omits URL-backed ELO/product images from self-contained exports. */
  excludeEloImages?: boolean;
  displayMode?: import("../types/C1_ModelTypes").C1_DisplayMode;
  eloItemScale?: number;
};

export class C1_State extends BaseObject {
  override get className(): string {
    return "C1_State";
  }

  #appState: State;
  readonly catalog: C1_Catalog;

  get project(): C1_Project {
    return this.#appState.c1Project;
  }

  get projectMetadata() {
    return this.#appState.projectMetadata;
  }
  readonly objectRegistry = new C1_ObjectRegistry();
  /** Plan-owned navigator action; present only while the C1 editor is mounted. */
  zoomNavigatorObject?: (object: BaseModel) => boolean;
  revealNavigatorObject?: (object: BaseModel) => void;

  readonly zoom = new WatchableValue(1, "zoom");
  readonly minZoom = new WatchableValue(0.2, "minZoom");
  readonly currentFloorId = new WatchableValue("", "currentFloorId");
  readonly editMode = new WatchableValue<EditMode>("building", "editMode");
  /** Direct access to the application's single selected ESTADA object. */
  get selectedObject(): BaseModel | undefined {
    return this.#appState.selectionManager.selectedObject;
  }

  set selectedObject(value: BaseModel | undefined) {
    this.#appState.selectionManager.selectedObject = value;
  }

  /** C1 facet of the globally selected ESTADA object. */
  get selection(): C1_BaseObject<any> | undefined {
    return this.selectedObject?.c1;
  }
  /** Walls are persistent ESTADA objects rather than C1 facets. */
  get selectedWall(): C1_Wall | undefined {
    return this.selectedObject instanceof C1_Wall && !this.selectedObject.isDeleted ? this.selectedObject : undefined;
  }

  setWallSelection(wall: C1_Wall) {
    this.setSelection(undefined);
    this.selectedObject = wall;
  }
  readonly justPlacedObject = new WatchableValue<C1_BaseObject<any> | undefined>(undefined, "justPlacedObject");
  readonly hoveredEloOwner = new WatchableValue<C1_BaseObject<any> | undefined>(undefined, "hoveredEloOwner");
  eloSelectionFocus: C1_BaseObject<any> | undefined;
  readonly categorieSelectionRoom = new WatchableValue<C1_Room | undefined>(undefined, "categorieSelectionRoom");
  readonly viewOffset = new WatchableValue({ x: 0, y: 0 }, "viewOffset");
  readonly focusedPlanRoom = new WatchableValue<C1_Room | undefined>(undefined, "focusedPlanRoom");
  readonly dragView = new WatchableValue<Rect | null>(null, "dragView");
  readonly roomDialogOpen = new WatchableValue(false, "roomDialogOpen");
  readonly deleteWallDialogId = new WatchableValue<string | null>(null, "deleteWallDialogId");
  readonly roomDialogDocked = new WatchableValue(false, "roomDialogDocked");
  readonly furnitureDialogOpen = new WatchableValue(false, "furnitureDialogOpen");
  readonly pdfOverlay = new WatchableValue<import("../lib/C1_pdfOverlay").C1_PdfOverlay | null>(null, "pdfOverlay");
  readonly pdfOverlayOpacity = new WatchableValue(50, "pdfOverlayOpacity");
  readonly pdfOverlayMoveMode = new WatchableValue(false, "pdfOverlayMoveMode");
  readonly paletteFurnitureId = new WatchableValue<string | null>(null, "paletteFurnitureId");
  private planClipboard?:
    | { kind: "furniture"; clipboard: C1_FurnitureClipboard }
    | { kind: "opening"; clipboard: C1_OpeningClipboard };
  private planClipboardProject?: C1_Project;
  private pasteBusy = false;
  private planCopyRequest = 0;
  /** A detached copy attached to the normal palette placement tools until click or cancel. */
  readonly pendingPaste = new WatchableValue<typeof this.planClipboard | null>(null, "pendingPaste");
  readonly paletteExistingDeviceId = new WatchableValue<string | null>(null, "paletteExistingDeviceId");
  readonly unplacedRoomChapterRequest = new WatchableValue<{roomId: string; requestId: number} | null>(null, "unplacedRoomChapterRequest");
  readonly furnitureCategoryScrollRequest = new WatchableValue<{
    category: FurnitureCategory;
    requestId: number;
  } | null>(null, "furnitureCategoryScrollRequest");
  readonly paletteDragTool = new WatchableValue<PaletteTool | null>(null, "paletteDragTool");
  readonly dropPreview = new WatchableValue<DropPreview | null>(null, "dropPreview");
  readonly saveState = new WatchableValue<SaveState>("idle", "saveState");
  readonly leaveDialogOpen = new WatchableValue(false, "leaveDialogOpen");
  readonly eloItemsVisible = new WatchableValue(false, "eloItemsVisible");
  readonly modeEditorDocked = new WatchableValue(true, "modeEditorDocked");
  get modeEditorClosedForSelectionId() { return this.#appState.dockManager.modeEditorClosedForSelectionId; }
  readonly modeEditorTab = new WatchableValue<"general" | "electrical">("general", "modeEditorTab");
  get buttonEditDialogItemId() { return this.#appState.dockManager.buttonEditDialogItemId; }
  get lightEditDialogItemId() { return this.#appState.dockManager.lightEditDialogItemId; }
  readonly showDebugFurnitureOrigins = new WatchableValue(
    initialDebugFlags.showDebugFurnitureOrigins,
    "showDebugFurnitureOrigins",
  );
  readonly showDebugEloItemPositions = new WatchableValue(
    initialDebugFlags.showDebugEloItemPositions,
    "showDebugEloItemPositions",
  );
  readonly showDebugEloGroupPositions = new WatchableValue(
    initialDebugFlags.showDebugEloGroupPositions,
    "showDebugEloGroupPositions",
  );
  readonly floorImageRenderOptions = new WatchableValue<C1_FloorImageRenderOptions | null>(
    null,
    "floorImageRenderOptions",
  );
  /** Catalog language only; UI translations and formatting use the main application's locale. */
  get language(): C1_Language {
    return toC1Language(this.#appState.settings.language);
  }

  requestFurnitureCategoryScroll(category: FurnitureCategory) {
    this.furnitureCategoryScrollRequest.value = {
      category,
      requestId: (this.furnitureCategoryScrollRequest.value?.requestId ?? 0) + 1,
    };
  }
  readonly roombookViewMode = new WatchableValue<"functions" | "groups">("functions", "roombookViewMode");
  readonly roomDraft = new WatchableValue<RoomDraft>(
    {
      name: "Neuer Raum",
      type: RoomType.Office,
      width: 3,
      height: 2.5,
      floorFinish: C1_FloorFinish.Parquet1,
    },
    "roomDraft",
  );
  readonly drag = new WatchableValue<Drag | null>(null, "drag");
  readonly objectDragPreview = new WatchableValue<ObjectDragPreview | null>(null, "objectDragPreview");
  readonly resizePreview = new WatchableValue<ResizePreview | null>(null, "resizePreview");

  private transactionDepth = 0;
  private pendingChange = false;
  private saveStateTimer?: ReturnType<typeof setTimeout>;

  readonly eventLogger = new EventLocker();

  constructor(appState: State) {
    super({ storeInObjectRegistry: false, isPersistent: false });
    this.#appState = appState;
    this.pdfOverlayOpacity.value = appState.settings.pdfBackgroundOpacity;
    appState.settings.onChange.subscribe((_object, property) => {
      if (property === "language" || property === "floorsHaveIndividualAxes") this.raiseOnUpdateUi(property);
      if (property === "c1TagByIndex" || property === "c1TagEditable") this.raiseOnUpdateUi(property);
      if (property === "pdfBackgroundOpacity") this.pdfOverlayOpacity.value = appState.settings.pdfBackgroundOpacity;
      if (property === "mainEditMode" && appState.settings.applicationMode === ApplicationMode.Main)
        this.editMode.value = appState.settings.mainEditMode;
    });
    if (appState.settings.applicationMode === ApplicationMode.Main) {
      this.editMode.value = appState.settings.mainEditMode;
    }
    this.catalog = loadDefinitions();
    this.zoom.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.hoveredEloOwner.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.minZoom.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.currentFloorId.onChange.subscribe((object, property) => {
      this.cancelPastePlacement();
      this.onChange.raise(object, property);
    });
    this.editMode.onChange.subscribe((object, property) => {
      this.cancelPastePlacement();
      this.hoveredEloOwner.value = undefined;
      this.eloSelectionFocus = undefined;
      if (appState.settings.applicationMode === ApplicationMode.Main) {
        appState.settings.mainEditMode = this.editMode.value;
        if (this.editMode.value !== appState.settings.mainEditMode) {
          this.editMode.value = appState.settings.mainEditMode;
          return;
        }
      }
      if (this.editMode.value === "furnishing" && this.selection instanceof C1_Room) {
        this.categorieSelectionRoom.value = this.selection;
        this.requestFurnitureCategoryScroll(furnitureCategoryForRoomType(this.selection.type));
      }
      this.onChange.raise(object, property);
    });
    this.#appState.selectionManager.onChange.subscribe((object, property) => {
      this.eloSelectionFocus = nextEloSelectionFocus(this.selection, this.eloSelectionFocus);
      this.onChange.raise(object, property);
    });
    this.#appState.undoManager.onChange.subscribe(() => this.handleUndoChange());
    this.justPlacedObject.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.categorieSelectionRoom.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.viewOffset.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.focusedPlanRoom.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.dragView.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.roomDialogOpen.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.roomDialogDocked.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.furnitureDialogOpen.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.pdfOverlay.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.pdfOverlayMoveMode.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.pdfOverlayOpacity.onChange.subscribe((object, property) => {
      appState.settings.pdfBackgroundOpacity = this.pdfOverlayOpacity.value;
      this.onChange.raise(object, property);
    });
    this.paletteFurnitureId.onChange.subscribe((object, property) => {
      this.pendingPaste.value = null;
      this.paletteExistingDeviceId.value = null;
      this.onChange.raise(object, property);
    });
    this.paletteExistingDeviceId.onChange.subscribe((object, property) => {
      if (this.paletteExistingDeviceId.value) this.pendingPaste.value = null;
      this.onChange.raise(object, property);
    });
    this.furnitureCategoryScrollRequest.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.unplacedRoomChapterRequest.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.paletteDragTool.onChange.subscribe((object, property) => {
      this.pendingPaste.value = null;
      this.onChange.raise(object, property);
    });
    this.pendingPaste.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.dropPreview.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.saveState.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.leaveDialogOpen.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.eloItemsVisible.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.modeEditorDocked.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.modeEditorClosedForSelectionId.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.modeEditorTab.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.showDebugFurnitureOrigins.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.showDebugEloItemPositions.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.showDebugEloGroupPositions.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    const persistDebugFlags = () => {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(
          DEBUG_FLAGS_STORAGE_KEY,
          JSON.stringify({
            showDebugFurnitureOrigins: this.showDebugFurnitureOrigins.value,
            showDebugEloItemPositions: this.showDebugEloItemPositions.value,
            showDebugEloGroupPositions: this.showDebugEloGroupPositions.value,
          } satisfies C1_DebugFlags),
        );
      } catch {
        // Debug preferences are optional when localStorage is unavailable.
      }
    };
    this.showDebugFurnitureOrigins.onChange.subscribe(persistDebugFlags);
    this.showDebugEloItemPositions.onChange.subscribe(persistDebugFlags);
    this.showDebugEloGroupPositions.onChange.subscribe(persistDebugFlags);
    this.floorImageRenderOptions.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.roombookViewMode.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.roomDraft.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.drag.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.objectDragPreview.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    this.resizePreview.onChange.subscribe((object, property) => this.onChange.raise(object, property));
    appState.dockManager.bind(this);
  }

  // static fromJSON(input: unknown): C1_State {
  //   const state = new C1_State();
  //   state.loadProjectFromJSONObject(input);
  //   return state;
  // }

  // loadProjectFromJSONObject(source: any) {
  //   const project = new C1_Project({
  //     id: source.id,
  //     projectName: source.projectName ?? "",
  //     updatedAt: source.updatedAt ?? new Date().toISOString(),
  //     northAngle: source.northAngle ?? 0,
  //     houseType: source.houseType ?? "detachedHouse",
  //     buildingWidth: source.buildingWidth ?? 12,
  //     buildingLength: source.buildingLength ?? 12,
  //     floorDistance: source.floorDistance ?? 3,
  //     equipmentStandard: source.equipmentStandard ?? 4,
  //     selectedProductManufacturers: source.selectedProductManufacturers ?? {},
  //   });

  //   this.project = project;
  //   project.fromJSON(source);
  //   this.currentFloorId.value =
  //     source.currentFloorId && this.getFloor(source.currentFloorId)
  //       ? source.currentFloorId
  //       : (this.floors[0]?.id ?? "");
  //   this.changed("state");
  // }

  // async saveProject(): Promise<boolean> {
  //   this.saveState.value = "saving";
  //   try {
  //     const saved = await saveMainProject();
  //     if (!saved) {
  //       this.saveState.value = "error";
  //       return false;
  //     }
  //     this.eventLogger.addEvent(C1_EventKey.ProjectSaved, {
  //       projectId: this.project.id,
  //       projectName: this.project.projectName,
  //     });

  //     this.eventLogger.flush();

  //     state.undoManager.clear();
  //     this.saveState.value = "saved";
  //     if (this.saveStateTimer !== undefined) clearTimeout(this.saveStateTimer);
  //     this.saveStateTimer = setTimeout(() => {
  //       this.saveState.value = "idle";
  //       this.saveStateTimer = undefined;
  //     }, 1800);
  //     return true;
  //   } catch {
  //     this.saveState.value = "error";
  //     return false;
  //   }
  // }

  get projectName() {
    return this.project.projectName;
  }

  get settings() {
    return this.project.settings;
  }

  get allAxes() { return this.#appState.axes.asArray(); }

  get axes() {
    return this.project.axesForFloor(this.currentFloorId.value);
  }

  get floors(): Array<C1_Floor> {
    return this.project.floors;
  }

  get px() {
    return 70 * this.zoom.value;
  }

  get svgPx() {
    return 70;
  }

  get currentBounds() {
    return this.project.floorsHaveIndividualAxes
      ? this.project.boundsForFloor(this.currentFloorId.value) : this.project.bounds;
  }

  viewFromBounds(bounds: Rect = this.currentBounds): Rect {
    const exportingFloorImage = this.floorImageRenderOptions.value !== null;
    const room = this.focusedPlanRoom?.value;
    if (!exportingFloorImage && room && !room.owner?.isDeleted) {
      const rect = room.rect();
      // A room plan needs local context, without the other rooms or desktop editor clearance.
      const margin = 1.5;
      return { x1: rect.x1 - margin, y1: rect.y1 - margin, x2: rect.x2 + margin, y2: rect.y2 + margin };
    }
    if (!exportingFloorImage && this.pdfOverlayOpacity.value > 0 &&
      (this.editMode.value === "building" || this.editMode.value === "furnishing")) {
      const floor = this.project.getFloor(this.currentFloorId.value);
      const overlay = this.pdfOverlay.value;
      if (isPdfOverlayActive(floor, overlay)) bounds = pdfDrawingBounds(bounds, floor?.pdfSource,
        floor?.pdfSource?.pageSize ?? overlay ?? undefined);
    }
    // The export margin is part of the drawing area, not the outer paper margin.
    // It leaves room for exterior wall thickness, opening tags and the north arrow.
    const planMargin = exportingFloorImage ? (this.floorImageRenderOptions.value?.includeDimensions ? 2.1 : 1.75) : 2.8;
    const editorClearance = exportingFloorImage ? 0 : 3;
    return {
      x1: bounds.x1 - planMargin,
      y1: bounds.y1 - planMargin,
      x2: bounds.x2 + planMargin + editorClearance,
      y2: bounds.y2 + planMargin + editorClearance,
    };
  }

  get view(): Rect {
    const baseView = this.dragView.value ?? this.viewFromBounds();
    const offset = this.viewOffset.value;
    return {
      x1: baseView.x1 + offset.x,
      y1: baseView.y1 + offset.y,
      x2: baseView.x2 + offset.x,
      y2: baseView.y2 + offset.y,
    };
  }

  get canvasWidth() {
    const view = this.view;
    return (view.x2 - view.x1) * this.px;
  }

  get canvasHeight() {
    const view = this.view;
    return (view.y2 - view.y1) * this.px;
  }

  get svgCanvasWidth() {
    const view = this.view;
    return (view.x2 - view.x1) * this.svgPx;
  }

  get svgCanvasHeight() {
    const view = this.view;
    return (view.y2 - view.y1) * this.svgPx;
  }

  get isDirty() {
    return this.#appState.undoManager.canUndo;
  }

  resetViewOffset() {
    this.viewOffset.value = { x: 0, y: 0 };
  }

  resetRoomDraft() {
    this.roomDraft.value = {
      name: "Neuer Raum",
      type: RoomType.Office,
      width: 3,
      height: 2.5,
      floorFinish: C1_FloorFinish.Parquet1,
    };
  }

  setProjectName(projectName: string) {
    this.project.setProjectName(projectName);
  }

  transaction(action: () => void) {
    this.transactionDepth++;
    try {
      action();
    } finally {
      this.transactionDepth--;
      if (!this.transactionDepth && this.pendingChange) {
        this.pendingChange = false;
        this.onChange.raise("transaction", this);
      }
    }
  }

  _currentFloorOwner: Floor | undefined;

  /** Refreshes history controls and leaves floors removed by undo or a canceled import. */
  private handleUndoChange() {
    if (this._currentFloorOwner && !this.project.getFloor(this._currentFloorOwner.id)) {
      const floor = this.project.floors[0];
      this._currentFloorOwner = floor?.owner;
      this.currentFloorId.value = floor?.id ?? "";
    }
    this.raiseOnChangeUI("undo");
  }

  get currentFloor(): C1_Floor {
    if (!this._currentFloorOwner) {
      const floor = this.project.createDefaultFloor();
      this._currentFloorOwner = floor.owner;
      return floor;
    }

    if (!this._currentFloorOwner.c1) {
      this._currentFloorOwner.c1 = new C1_Floor({}, this._currentFloorOwner);
      return this._currentFloorOwner.c1;
    }

    return this._currentFloorOwner.c1;
  }

  /** Re-registers runtime-only C1 objects removed by the global project reset. */
  registerRuntimeObjects(): void {
    this.catalog.registerDefinitionsInObjectRegistry();
  }

  /** Selects an existing project floor and synchronizes its ESTADA owner. */
  setCurrentFloor(id: string): boolean {
    const floor = this.#appState.floors.findFirst((candidate) => candidate.id === id);
    if (!floor?.c1) return false;
    this._currentFloorOwner = floor;
    this.currentFloorId.value = floor.id;
    return true;
  }

  setVisibleFloor(id?: string) {
    if (id === this.currentFloorId.value) return;

    this.transaction(() => {
      this.floors.forEach((floor) => floor.setVisible(floor.id === id));
    });
  }

  /** The mobile inline editor can consume plan taps while assigning a key. */
  handlePlanSelection?: (object: BaseModel | undefined) => boolean;

  setSelection = (selection: C1_BaseObject<any> | undefined, justPlaced = false) => {
    if (!justPlaced && this.handlePlanSelection?.(selection?.owner as BaseModel | undefined)) return;
    // A deliberate click can reopen the editor of the same object after closing it.
    if (selection && !justPlaced) this.#appState.dockManager.reopenSelection();
    if (this.hoveredEloOwner) this.hoveredEloOwner.value = undefined;
    this.justPlacedObject.value = justPlaced ? selection : undefined;
    const currentSelection = this.selection;
    if (currentSelection instanceof C1_Room && currentSelection !== selection) {
      currentSelection.firstSelection = false;
    }

    this.selectedObject = selection?.owner as BaseModel | undefined;
  };

  isDeletionProtectedInDetailMode(object: BaseModel | undefined): boolean {
    if (this.editMode.value !== "EloDetail" || !object) return false;
    return object.className === "Room" || ["C1_Room", "C1_Opening", "C1_Furniture"].includes(object.c1?.className ?? "");
  }

  canDeleteSelection(selection = this.selection): boolean {
    const object = this.selectedObject;
    if (this.isDeletionProtectedInDetailMode(object)) return false;
    if (!object || object.isDeleted) return false;
    if (object instanceof C1_Wall) return this.editMode.value === "building" && (object.freeWall || wallMergeRooms(object) !== undefined);
    if (!object.canBeDeletedInteractive() && !object.getDeleteConfirmText()) return false;
    // Connections have no C1 facet and can also be removed in interaction mode.
    if (this.selectedObject instanceof Connection) return !this.selectedObject.isDeleted;
    if (!selection) return false;
    if (this.editMode.value === "interactionConcept") return false;
    if ((this.editMode.value !== "EloConcept" && this.editMode.value !== "EloDetail")) return true;
    if (selection instanceof C1_EloItem) return true;
    return selection instanceof C1_Furniture && isElectricalFurnitureId(selection.definitionId);
  }

  /** Routes editor buttons and keyboard deletion through the same policy as the navigator. */
  deleteSelection() {
    if (!this.canDeleteSelection()) {
      if (this.selectedObject && !this.selectedObject.isDeleted)
        showDeletionPreventedToast(this.selectedObject, this.editMode.value);
      return;
    }
    return this.#appState.menubarActions.onDelete(this.selectedObject);
  }

  private get copyableSelection(): C1_Furniture | C1_Opening | undefined {
    const selection = this.selection;
    if (selection instanceof C1_Furniture || selection instanceof C1_Opening) return selection;
    return selection instanceof C1_Consumer ? selection.lightGroup : undefined;
  }

  private get copyableDevice(): C1_Consumer | C1_Button | undefined {
    const selection = this.selection;
    return selection instanceof C1_Consumer || selection instanceof C1_Button ? selection : undefined;
  }

  private get copyableDeviceRoom(): C1_Room | undefined {
    const owner = this.copyableDevice?.owner as BaseModel | undefined;
    const room = owner?.parentRoom?.c1;
    return room instanceof C1_Room ? room : undefined;
  }

  canCopy(): boolean {
    return !this.pasteBusy && !!(this.copyableSelection?.room || this.copyableDeviceRoom) && !this.selectedObject?.isDeleted;
  }

  canPaste(): boolean {
    return !!this.planClipboard && this.planClipboardProject === this.project &&
      !this.#appState.projectMetadata.readOnly && !this.pasteBusy &&
      !!this.project.getFloor(this.currentFloorId.value)?.rooms.length &&
      !this.#appState.undoManager.isCompletingInteraction;
  }

  async copySelected(): Promise<void> {
    const selection = this.copyableSelection;
    if (!this.canCopy()) return;
    const request = ++this.planCopyRequest;
    if (!selection) {
      const device = this.copyableDevice;
      const owner = device?.owner;
      const room = this.copyableDeviceRoom;
      if (!device || !owner || !room) return;
      const project = this.project;
      const paletteId = paletteIdForExistingDevice(owner as import("../../../src/models/Consumer").default | import("../../../src/models/Button").default);
      const definition = furnitureResolvers.definition?.(paletteId);
      if (!definition) return;
      this.planClipboard = undefined;
      this.raiseOnChange();
      try {
        await this.#appState.menubarActions.onCopy(owner as BaseModel);
        const data = this.#appState.clipboard.getData();
        if (!data || this.project !== project || request !== this.planCopyRequest) return;
        this.planClipboard = { kind: "furniture", clipboard: {
          roomId: room.id,
          native: { data, className: owner.className, name: owner.name },
          data: { definitionId: paletteId, relativePosition: { x: .5, y: .5 },
            reflect: device.data.interactivePlacement?.reflect,
            baseProps: { width: definition.defaultWidth, height: definition.defaultHeight,
              rotation: device.data.interactivePlacement?.rotation ?? 0 } },
        } };
        this.planClipboardProject = project;
        this.raiseOnChange();
      } catch (error) { showErrToast(String(error), itx("c1.clipboard.pasteFailed", "Element konnte nicht eingefügt werden")); }
      return;
    }
    this.planClipboard = selection instanceof C1_Furniture
      ? { kind: "furniture", clipboard: copyFurniture(selection) }
      : { kind: "opening", clipboard: copyOpening(selection) };
    this.planClipboardProject = this.project;

    this.raiseOnChange();
  }

  pasteCopied(): void {
    if (!this.planClipboard || !this.canPaste()) return;
    const entry = structuredClone(this.planClipboard);
    this.cancelPastePlacement();
    this.paletteDragTool.value = null;
    this.paletteFurnitureId.value = null;
    this.paletteExistingDeviceId.value = null;
    this.dropPreview.value = null;
    this.setSelection(undefined);
    if (entry.kind === "furniture") this.paletteFurnitureId.value = entry.clipboard.data.definitionId;
    else this.paletteDragTool.value = entry.clipboard.data.kind;
    // Set after the tools, whose change handlers clear any previous paste draft.
    this.pendingPaste.value = entry;
  }

  cancelPastePlacement(): void {
    if (!this.pendingPaste.value) return;
    this.pendingPaste.value = null;
    this.paletteDragTool.value = null;
    this.paletteFurnitureId.value = null;
    this.paletteExistingDeviceId.value = null;
    this.dropPreview.value = null;
  }

  /** Commit exactly the preview the user clicked, as one reversible creation. */
  async placeCopied(preview: DropPreview): Promise<boolean> {
    const entry = this.pendingPaste.value;
    if (!entry || !this.canPaste() || !("room" in preview) || !preview.room ||
      !this.project.getFloor(this.currentFloorId.value)?.rooms.includes(preview.room)) return false;
    const room = preview.room;
    if (entry.kind !== preview.type) return false;
    try {
      if (entry.kind === "furniture" && entry.clipboard.native && preview.type === "furniture") {
        this.pasteBusy = true;
        this.raiseOnChange();
        const pasted = await placeNativeClipboard(entry.clipboard, room, preview);
        this.setSelection(pasted, true);
        this.justPlacedObject.value = pasted;
        this.cancelPastePlacement();
        return true;
      }
      this.#appState.undoManager.runAsSingleStep(itx("c1.clipboard.pasteElement", "Element einfügen"), () => {
        try {
          const pasted = entry.kind === "furniture" && preview.type === "furniture"
            ? pasteFurniture(entry.clipboard, room, preview)
            : entry.kind === "opening" && preview.type === "opening"
              ? pasteOpening(entry.clipboard, room, preview.opening) : undefined;
          if (!pasted) throw new Error("Invalid paste placement.");
          generateTags(this.project);
          this.setSelection(pasted, true);
          this.justPlacedObject.value = pasted;
        } catch (error) {
          this.#appState.undoManager.rollbackCurrentStep();
          throw error;
        }
      });
      this.cancelPastePlacement();
      return true;
    } catch (error) {
      showErrToast(String(error), itx("c1.clipboard.pasteFailed", "Element konnte nicht eingefügt werden"));
      return false;
    } finally { this.pasteBusy = false; this.raiseOnChange(); }
  }

  requestWallDeletion(object: BaseModel): boolean {
    if (object instanceof C1_Wall && object.freeWall && this.editMode.value === "building") {
      state.undoManager.runAsSingleStep(itx("c1.freeWall.delete", "Freie Wand löschen"), () => object.delete());
      return true;
    }
    if (object instanceof C1_Wall && this.editMode.value === "building" && wallMergeRooms(object)) {
      this.deleteWallDialogId.value = object.id;
      return true;
    }
    return false;
  }

  undo() {
    this.#appState.undoManager.undo();
  }

  redo() {
    this.#appState.undoManager.redo();
  }

  addAxis(data: WithOptionalId<C1_AxisData>) {
    return this.project.addAxis(data);
  }

  removeAxis(id: string) {
    this.project.removeAxis(id);
  }

  replaceAxes(data: C1_AxisData[]) {
    this.project.replaceAxes(data);
  }

  toJSON(): C1_ProjectData {
    return this.project.toJSON();
  }

  // fromJSON(input: unknown) {
  //   this.loadProjectFromJSONObject(input);
  // }
}

registerC1StateFactory((appState) => new C1_State(appState));

export { c1_state as default } from "./C1_runtimeContext";
